import { useEffect, useMemo, useRef, useState } from 'react';
import { hotList, siteName, timeline } from '@/mocks/chigua';

const NAV = [
  { label: '今日速览', href: '#ts' },
  { label: '全网热榜', href: '#rank' },
  { label: '栏目导航', href: '#lanmu' },
  { label: '话题广场', href: '#huati' },
  { label: '专题深读', href: '#zhuanti' },
  { label: '关于我们', href: '#about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [focused, setFocused] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    const pool = [
      ...timeline.map((t) => ({ title: t.title, cat: t.cat, from: '今日速览' as const })),
      ...hotList.map((h) => ({ title: h.title, cat: h.cat, from: '全网热榜' as const })),
    ];
    return pool
      .filter((i) => i.title.toLowerCase().includes(kw.toLowerCase()))
      .slice(0, 6);
  }, [keyword]);

  const goResult = () => {
    setKeyword('');
    setSearchOpen(false);
    setFocused(false);
    document.getElementById('ts')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-background-50/90 shadow-sm backdrop-blur-md' : 'bg-background-50'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:h-[68px] md:px-6">
        {/* logo */}
        <a href="#top" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="heading-font text-lg font-extrabold tracking-wide text-foreground-950 md:text-xl">
              {siteName}
            </span>
            <span className="mt-0.5 text-[10px] tracking-[0.2em] text-foreground-500 md:text-[11px]">
              全网热点 · 一条线看尽
            </span>
          </span>
        </a>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="rounded-md px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700 whitespace-nowrap"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* search */}
          <div className="relative hidden sm:block">
            <button
              type="button"
              onClick={() => {
                setSearchOpen((v) => !v);
                setFocused(false);
              }}
              aria-label="搜索热点"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-11 w-72">
                <div className="flex items-center gap-2 rounded-lg border border-secondary-200 bg-background-50 px-3">
                  <i className="ri-search-line text-foreground-500"></i>
                  <input
                    ref={searchRef}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    placeholder="搜索热点、事件或话题"
                    className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                    aria-label="搜索"
                  />
                </div>
                {focused && keyword.trim() && (
                  <div className="mt-1 overflow-hidden rounded-lg border border-secondary-200 bg-background-50">
                    {results.length ? (
                      <ul>
                        {results.map((r) => (
                          <li key={r.title}>
                            <button
                              type="button"
                              onMouseDown={goResult}
                              className="flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-secondary-50"
                            >
                              <span className="mt-0.5 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900 whitespace-nowrap">
                                {r.from}
                              </span>
                              <span className="line-clamp-1 text-xs text-foreground-800">{r.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="px-3 py-3 text-xs text-foreground-500">没有找到相关热点，换个关键词试试</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <a
            href="#contact"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            投稿爆料
          </a>

          {/* hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-secondary-100 lg:hidden"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {menuOpen && (
        <div className="border-t border-secondary-100 bg-background-50 lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 md:px-6" aria-label="移动端导航">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setMenuOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-secondary-50"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setMenuOpen(false)}
              className="mt-1 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
            >
              投稿爆料
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}